import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import { useWordContext } from '../context/WordContext';
import { normalizeWord } from '../utils/wordUtils';
import './ChatbotDictionary.css'; // We'll create this CSS file specifically for chatbot dictionary
import './ChatbotDictionaryGrading.css'; // Import dedicated grading CSS

// Helper function to extract the specific sentence containing the target word
const extractSentenceContext = (fullText, targetWord) => {
  try {
    if (!fullText || !targetWord) return fullText;
    
    const targetIndex = fullText.indexOf(targetWord);
    
    if (targetIndex === -1) return fullText;
    
    // Find sentence boundaries (., !, ?)
    // Start by finding the beginning of the sentence
    let startIndex = targetIndex;
    while (startIndex > 0) {
      // Look for sentence-ending punctuation followed by space or start of text
      if (/[.!?]\s/.test(fullText.substring(startIndex - 1, startIndex + 1)) || 
          startIndex === 1) {
        break;
      }
      startIndex--;
    }
    
    // Find the end of the sentence
    let endIndex = targetIndex + targetWord.length;
    while (endIndex < fullText.length) {
      // Look for sentence-ending punctuation
      if (/[.!?]/.test(fullText[endIndex]) && 
          (endIndex + 1 === fullText.length || /\s/.test(fullText[endIndex + 1]))) {
        endIndex++; // Include the punctuation
        break;
      }
      endIndex++;
    }
    
    // Get the complete sentence
    return fullText.substring(startIndex, endIndex).trim();
  } catch (err) {
    logger.error(`ChatbotWordLookup - Error extracting context for "${targetWord}":`, err);
    return fullText;
  }
};

// Helper function to format definition text with special handling for parenthetical content
const formatDefinition = (text) => {
  if (!text) return '';
  
  // Find all parenthetical expressions
  const parts = [];
  let currentText = '';
  let inParentheses = 0;
  let parentheticalText = '';
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    
    if (char === '(') {
      if (inParentheses === 0) {
        // Save regular text before parentheses
        if (currentText) {
          parts.push(<span key={`text-${parts.length}`}>{currentText}</span>);
          currentText = '';
        }
        parentheticalText = '(';
      } else {
        parentheticalText += '(';
      }
      inParentheses++;
    } else if (char === ')' && inParentheses > 0) {
      parentheticalText += ')';
      inParentheses--;
      
      if (inParentheses === 0) {
        // Save complete parenthetical expression
        parts.push(<span key={`parens-${parts.length}`} className="chatbot-parenthetical">{parentheticalText}</span>);
        parentheticalText = '';
      }
    } else if (inParentheses > 0) {
      parentheticalText += char;
    } else {
      currentText += char;
    }
  }
  
  // Add any remaining text
  if (currentText) {
    parts.push(<span key={`text-${parts.length}`}>{currentText}</span>);
  }
  
  // Add any unclosed parenthetical content
  if (parentheticalText) {
    parts.push(<span key={`parens-${parts.length}`} className="chatbot-parenthetical">{parentheticalText}</span>);
  }
  
  return <>{parts}</>;
};

const ChatbotWordLookup = ({ word, children, language, sentence, onSelectWord }) => {
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastClickedWord, setLastClickedWord] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [wordGrade, setWordGrade] = useState(1);
  const [wordKnown, setWordKnown] = useState(false);
  const [justSavedWord, setJustSavedWord] = useState(false);
  
  // Use the context for saved words
  const { 
    isWordSaved, 
    addSavedWord, 
    removeSavedWord,
    refreshSavedWords,
    getWordGrade,
    isWordKnown,
    updateWordGrade,
    updateWordKnown,
    isKanjiSaved,
    isKanjiKnown,
    getKanjiGrade
  } = useWordContext();
  
  // Normalize the current word for checking
  const normalizedWord = normalizeWord(word);
  
  // Get current language
  const currentLanguage = language || localStorage.getItem('learningLanguage') || 'Spanish';
  
  // Check if the word is saved
  const checkSavedStatus = useCallback(() => {
    const exactMatch = isWordSaved(normalizedWord, currentLanguage);
    if (exactMatch || justSavedWord) {
      return true;
    }
    return false;
  }, [normalizedWord, currentLanguage, isWordSaved, justSavedWord]);
  
  // Check if the word is saved
  const [isSaved, setIsSaved] = useState(false);
  
  // Update the saved status when data changes
  useEffect(() => {
    const savedStatus = checkSavedStatus();
    setIsSaved(savedStatus);
  }, [checkSavedStatus, normalizedWord, currentLanguage, justSavedWord]);

  // Update local state when the word's saved status changes
  useEffect(() => {
    if (isSaved) {
      setWordGrade(getWordGrade(normalizedWord, currentLanguage));
      setWordKnown(isWordKnown(normalizedWord, currentLanguage));
    } else {
      setWordGrade(1);
      setWordKnown(false);
    }
  }, [isSaved, normalizedWord, currentLanguage, getWordGrade, isWordKnown]);

  // Reset justSavedWord when word changes
  useEffect(() => {
    // When the word changes, reset justSavedWord to false
    setJustSavedWord(false);
  }, [normalizedWord]);

  // Helper function to get userId from multiple sources
  const getUserId = () => {
    // Always try to get the latest userId from the JWT token first
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        // Decode JWT token to get user info
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        
        const decodedToken = JSON.parse(jsonPayload);
        if (decodedToken.sub) {
          const userId = decodedToken.sub;
          logger.debug(`ChatbotWordLookup - Extracted user ID from token: ${userId}`);
          localStorage.setItem('userId', userId);
          return userId;
        }
      } catch (err) {
        logger.error("ChatbotWordLookup - Error decoding token:", err);
      }
    }
    
    // Fall back to localStorage only if token extraction fails
    const localUserId = localStorage.getItem('userId');
    if (localUserId) {
      logger.debug("ChatbotWordLookup - Using userId from localStorage (fallback)");
      return localUserId;
    }
    
    logger.warn("ChatbotWordLookup - No userId available from token or localStorage");
    return null;
  };

  const fetchDefinition = async () => {
    // If we've already fetched this word and it's being reopened, don't fetch again
    if (lastClickedWord === normalizedWord && definition) {
      logger.debugComponent('chatbot', `Using cached definition for "${normalizedWord}"`);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Map full language names to language codes
      const languageMapping = {
        'Japanese': 'ja',
        'Spanish': 'es',
        'French': 'fr',
        'Italian': 'it',
        'German': 'de',
        'Portuguese': 'pt',
        'Chinese': 'zh',
        'Russian': 'ru',
        'Arabic': 'ar',
        'Korean': 'ko',
        'Dutch': 'nl',
        'Polish': 'pl',
        'Swedish': 'sv',
        'Turkish': 'tr',
        'Ukrainian': 'uk',
        'Vietnamese': 'vi',
        'Greek': 'el',
        'Hebrew': 'he',
        'Hindi': 'hi',
        'Thai': 'th'
      };
      
      // Get language code (lowercase) or use full language name if no mapping
      const languageCode = languageMapping[currentLanguage] || currentLanguage.toLowerCase();
      
      // For Japanese, we need to handle the API request differently
      if (currentLanguage === 'Japanese') {
        logger.debugComponent('chatbot', `Fetching definition for Japanese word "${word}"`);
        
        // Use our proxy endpoint for Japanese lookups
        const jishoResponse = await apiClient.get(
          `/api/jisho?keyword=${encodeURIComponent(word)}`
        );
        
        if (!jishoResponse.ok) {
          throw new Error(`Jisho API returned status ${jishoResponse.status}`);
        }
        
        const jishoData = await jishoResponse.json();
        logger.debugComponent('chatbot', `Received Jisho data for "${word}":`, jishoData);
        
        // Format the Jisho response into our expected structure
        if (jishoData.data && jishoData.data.length > 0) {
          const processedData = {
            definitions: jishoData.data[0].senses.map(sense => 
              sense.english_definitions.join('; ')
            ),
            reading: jishoData.data[0].japanese[0].reading,
            jlpt: jishoData.data[0].jlpt.join(', '),
            isJisho: true
          };
          setDefinition(processedData);
        } else {
          setDefinition({ definitions: [], isJisho: true });
        }
      } else {
        // Check if we have a sentence context
        if (sentence) {
          logger.debugComponent('chatbot', `Fetching context definition for "${word}" in sentence: "${sentence}"`);
          
          // Use the contextDefine endpoint when we have a sentence
          const response = await apiClient.get(
            `/api/contextDefine?word=${encodeURIComponent(word)}&sentence=${encodeURIComponent(sentence)}&lang=${languageCode}`
          );
          
          if (!response.ok) {
            logger.errorComponent('chatbot', `Context API error status ${response.status} for "${word}"`);
            throw new Error(`Context definition endpoint returned status ${response.status}`);
          }
          
          const result = await response.json();
          logger.debugComponent('chatbot', `Received context definition data for "${word}":`, result);
          
          // Validate and process the response
          processDefinitionResponse(response, result, word);
        } else {
          logger.debugComponent('chatbot', `Fetching standard definition for "${word}"`);
          
          // Use the regular define endpoint when we don't have a sentence
          const response = await apiClient.get(
            `/api/define?word=${encodeURIComponent(word)}&lang=${languageCode}`
          );
          
          if (!response.ok) {
            logger.errorComponent('chatbot', `API error status ${response.status} for "${word}"`);
            throw new Error(`Definition endpoint returned status ${response.status}`);
          }
          
          const result = await response.json();
          logger.debugComponent('chatbot', `Received definition data for "${word}":`, result);
          
          // Validate and process the response
          processDefinitionResponse(response, result, word);
        }
      }
      
      // Update the last clicked word to avoid unnecessary API calls
      setLastClickedWord(normalizedWord);
    } catch (err) {
      logger.errorComponent('chatbot', `Error fetching definition for "${word}":`, err);
      logger.error("ChatbotWordLookup - Error fetching definition:", err);
      
      // Create a more user-friendly error message
      const errorMessage = err.message || "Failed to fetch definition";
      setError(errorMessage);
      
      // Set minimal data to avoid null reference errors
      setDefinition({
        definitions: [`Could not load definition for "${word}". ${errorMessage}`]
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to process and validate definition response
  const processDefinitionResponse = (response, result, word) => {
    try {
      // Validate the response structure
      if (!result || typeof result !== 'object') {
        logger.errorComponent('chatbot', `Invalid API response format for "${word}":`, result);
        throw new Error('Invalid API response format');
      }
      
      // Make sure definitions exists and is an array
      if (!Array.isArray(result.definitions)) {
        logger.errorComponent('chatbot', `API response missing definitions array:`, result);
        result.definitions = result.definitions ? [String(result.definitions)] : ['No definition available'];
      }
      
      // Ensure all definitions are strings
      result.definitions = result.definitions.map(def => 
        typeof def === 'string' ? def : JSON.stringify(def)
      );
      
      // Check if we have parts_of_speech data
      if (result.parts_of_speech) {
        logger.debugComponent('chatbot', `Word "${word}" parts of speech:`, result.parts_of_speech);
        
        // If it's a noun, log additional info
        if (result.parts_of_speech.noun && result.noun_info) {
          logger.debugComponent('chatbot', `Noun info for "${word}":`, result.noun_info);
        }
        
        // If it's a verb, log additional info
        if (result.parts_of_speech.verb && result.verb_info) {
          logger.debugComponent('chatbot', `Verb info for "${word}":`, result.verb_info);
        }
      } else {
        logger.debugComponent('chatbot', `No parts of speech data for "${word}"`);
      }
      
      setDefinition(result);
    } catch (jsonError) {
      logger.errorComponent('chatbot', `JSON parse error for "${word}":`, jsonError);
      try {
        logger.debugComponent('chatbot', `Raw response text:`, result);
      } catch (textError) {
        logger.errorComponent('chatbot', `Could not get response text:`, textError);
      }
      setDefinition({ 
        definitions: [`Error parsing definition data for "${word}"`] 
      });
    }
  };

  // Handle click on word to send it to the dictionary panel
  const handleClick = () => {
    // If we're clicking a word, fetch its definition and notify the parent component
    if (!definition || normalizedWord !== lastClickedWord) {
      setLastClickedWord(normalizedWord);
      fetchDefinition();
    }
    
    // Extract the specific sentence containing the word before notifying the parent component
    const contextSentence = extractSentenceContext(sentence, word);
    logger.debugComponent('chatbot', `Fetching context definition for "${word}" in sentence: "${contextSentence}"`);
    
    // Notify the parent component about the selected word with the extracted sentence
    if (onSelectWord && typeof onSelectWord === 'function') {
      onSelectWord(word, contextSentence);
    }
  };

  // Handle accordion toggling - only one section can be open at a time
  const toggleAccordion = (section) => {
    setOpenAccordion(openAccordion === section ? null : section);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    try {
      // Get userId using helper function
      const userId = getUserId();
      
      if (!userId) {
        logger.error("ChatbotWordLookup - No user ID available, cannot save word");
        throw new Error("No user ID available. Please log in again.");
      }
      
      // Always use the normalized word (just removes periods and commas, keeps apostrophes)
      const wordToSave = normalizedWord;
      
      logger.debugComponent('chatbot', `Saving word: "${wordToSave}" for language: ${currentLanguage} with userId: ${userId}`);
      
      const result = await apiClient.post('/api/saveUserWord', {
        userId, 
        language: currentLanguage, 
        item: wordToSave
      });
      
      // Check if result is valid (apiClient.post now returns the parsed JSON directly)
      if (!result || !result.message) {
        logger.error("ChatbotWordLookup - Failed to save word: Invalid response");
        throw new Error("Failed to save word: Invalid response");
      }
      
      logger.debugComponent('chatbot', `Word saved successfully:`, result.message);
      
      // Update context
      addSavedWord(wordToSave, currentLanguage);
      
      // Set local state to force UI update immediately
      setJustSavedWord(true);
      
      // Set initial grade and known state
      setWordGrade(1);
      setWordKnown(false);
      
      // Force refresh saved words to ensure the list is up to date
      refreshSavedWords();
      
      // Dispatch an event to notify that word data has been updated
      // This will trigger the WordCountContext to update its counts
      window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
    } catch (error) {
      logger.error("ChatbotWordLookup - Error saving word:", error);
      setError("Failed to save word. Please try again.");
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    try {
      const userId = getUserId(); // Use helper function
      
      if (!userId) {
        logger.error("ChatbotWordLookup - No user ID available, cannot remove word");
        throw new Error("No user ID available. Please log in again.");
      }
      
      // Always use the normalized word (just removes periods and commas, keeps apostrophes)
      const wordToDelete = normalizedWord;
      
      logger.debugComponent('chatbot', `Removing saved word: "${wordToDelete}" for language: ${currentLanguage}`);
      
      const result = await apiClient.post('/api/removeUserWord', {
        userId, 
        language: currentLanguage, 
        item: wordToDelete
      });
      
      // Check if result is valid (apiClient.post now returns the parsed JSON directly)
      if (!result || !result.message) {
        logger.error("ChatbotWordLookup - Failed to remove word: Invalid response");
        throw new Error("Failed to remove word: Invalid response");
      }
      
      logger.debugComponent('chatbot', `Word removed successfully:`, result.message);
      
      // Update context
      removeSavedWord(wordToDelete, currentLanguage);
      
      // Reset the justSavedWord state
      setJustSavedWord(false);
      
      // Force refresh saved words
      refreshSavedWords();
      
      // Dispatch an event to notify that word data has been updated
      // This will trigger the WordCountContext to update its counts
      window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
    } catch (error) {
      logger.error("ChatbotWordLookup - Error removing word:", error);
      setError("Failed to remove word. Please try again.");
    }
  };

  const handleGradeChange = async (e) => {
    e.stopPropagation();
    const newGrade = parseInt(e.target.value, 10);
    setWordGrade(newGrade);
    
    if (isSaved) {
      try {
        const success = await updateWordGrade(normalizedWord, currentLanguage, newGrade);
        if (!success) {
          logger.error("ChatbotWordLookup - Failed to update word grade");
          setError("Failed to update word grade. Please try again.");
        }
      } catch (error) {
        logger.error("ChatbotWordLookup - Error updating word grade:", error);
        setError("Failed to update word grade. Please try again.");
      }
    }
  };

  const handleKnownChange = async (e) => {
    e.stopPropagation();
    const isKnown = e.target.checked;
    setWordKnown(isKnown);
    
    if (isSaved) {
      try {
        const success = await updateWordKnown(normalizedWord, currentLanguage, isKnown);
        if (!success) {
          logger.error("ChatbotWordLookup - Failed to update word known status");
          setError("Failed to update word status. Please try again.");
        }
      } catch (error) {
        logger.error("ChatbotWordLookup - Error updating word known status:", error);
        setError("Failed to update word status. Please try again.");
      }
    }
  };

  const renderJapaneseDefinition = () => (
    <div>
      <strong>{word}</strong>
      <hr className="chatbot-definition-separator" />
      <div className="chatbot-definition-content">
        {definition.reading && <p className="chatbot-reading">{definition.reading}</p>}
        {definition.jlpt && <p className="chatbot-jlpt">JLPT: {definition.jlpt}</p>}
        <ul>
          {definition.definitions.length > 0 ? 
            definition.definitions.map((def, index) => (
              <li key={index}>{formatDefinition(def)}</li>
            )) : 
            <li>No definition found.</li>
          }
        </ul>
      </div>
    </div>
  );

  const renderStandardDefinition = () => {
    // First check if we have valid data
    if (!definition) {
      return (
        <div>
          <strong>{word}</strong>
          <hr className="chatbot-definition-separator" />
          <p>No definition data available.</p>
        </div>
      );
    }

    // Remove or conditionally limit the console log that's causing spam
    // Only log this when the definition is first loaded, not on every render
    if (definition && !definition.logged) {
      logger.debugComponent('chatbot', `Rendering definition for "${word}":`, definition);
      definition.logged = true;
    }
    
    // Group definitions by word type if they follow the format "type: definition"
    const groupedDefinitions = {};
    
    if (definition.definitions && definition.definitions.length > 0) {
      // Ensure all definitions are strings
      const stringDefinitions = definition.definitions.map(def => 
        typeof def === 'string' ? def : JSON.stringify(def)
      );
      
      stringDefinitions.forEach(def => {
        // Check if the definition includes a word type (e.g., "noun: definition")
        const match = def.match(/^([\w\s]+):(.*)/i);
        
        if (match) {
          const wordType = match[1].trim().toLowerCase();
          let definitionContent = match[2].trim();
          
          // More intelligent comma-based split that respects parentheses
          // This prevents splitting "new (plural, masculine)" into separate definitions
          const definitions = [];
          let currentDef = '';
          let inParentheses = 0;
          
          // Go through character by character
          for (let i = 0; i < definitionContent.length; i++) {
            const char = definitionContent[i];
            
            // Track parentheses
            if (char === '(') inParentheses++;
            else if (char === ')') inParentheses--;
            
            // If comma found and not inside parentheses, end definition
            if (char === ',' && inParentheses === 0) {
              if (currentDef.trim()) {
                definitions.push(currentDef.trim());
              }
              currentDef = '';
            } else {
              currentDef += char;
            }
          }
          
          // Add the last definition if there is one
          if (currentDef.trim()) {
            definitions.push(currentDef.trim());
          }
          
          if (!groupedDefinitions[wordType]) {
            groupedDefinitions[wordType] = [];
          }
          
          // Add each definition individually
          groupedDefinitions[wordType].push(...definitions);
        } else {
          // If no word type is found, add to "other" category
          if (!groupedDefinitions.other) {
            groupedDefinitions.other = [];
          }
          groupedDefinitions.other.push(def);
        }
      });
    } else {
      // If no definitions array, create a default "other" category
      groupedDefinitions.other = ["No definition available"];
    }
    
    // Order of word types to display
    const wordTypeOrder = [
      'noun', 'masculine noun', 'feminine noun', 'plural noun',
      'verb', 'transitive verb', 'intransitive verb', 'reflexive verb',
      'adjective', 'adverb', 'pronoun', 
      'preposition', 'conjunction', 'interjection', 'article', 'determiner', 'other'
    ];
    
    // Sort word types by the predefined order
    const sortedWordTypes = Object.keys(groupedDefinitions).sort((a, b) => {
      const indexA = wordTypeOrder.indexOf(a);
      const indexB = wordTypeOrder.indexOf(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
    
    // Clean up the display word (remove periods and commas but keep apostrophes)
    const displayWord = word.replace(/[.,]/g, '');
    
    // Check if the word is an infinitive verb
    // Normalize both the current word and the infinitive form for comparison
    const normalizedDisplayWord = normalizeWord(displayWord);
    let isInfinitiveVerb = false;
    
    if (definition.verb_info && definition.verb_info.infinitive_form) {
      const infinitiveForm = typeof definition.verb_info.infinitive_form === 'string' 
        ? definition.verb_info.infinitive_form 
        : String(definition.verb_info.infinitive_form);
          
      // Multiple ways to check for a match
      const normalizedInfinitive = normalizeWord(infinitiveForm);
      
      // Direct match
      const directMatch = (normalizedDisplayWord === normalizedInfinitive);
      
      // Check if the current word ends with the infinitive marker (e.g., "er", "ir", "re" in French)
      const hasInfinitiveEnding = /er$|ir$|re$/.test(normalizedDisplayWord);
      
      // If the word itself has an infinitive ending AND it matches the provided infinitive form
      isInfinitiveVerb = directMatch || 
        (hasInfinitiveEnding && 
         (normalizedDisplayWord === normalizedInfinitive || 
          normalizedDisplayWord.includes(normalizedInfinitive) || 
          normalizedInfinitive.includes(normalizedDisplayWord)));
      
      if (isInfinitiveVerb) {
        logger.debugComponent('chatbot', `Word "${normalizedDisplayWord}" is detected as an infinitive verb matching "${normalizedInfinitive}"`);
      }
    }
    
    return (
      <div>
        <strong>{displayWord}</strong>
        <hr className="chatbot-definition-separator" />
        
        <div className="chatbot-definition-content">
          {sortedWordTypes.length > 0 ? (
            // Display each word type and its definitions
            sortedWordTypes.map(wordType => (
              <div key={wordType} className="chatbot-word-type-section">
                <h4 className="chatbot-word-type">{wordType !== 'other' ? wordType : ''}</h4>
                <ul>
                  {groupedDefinitions[wordType].map((def, index) => (
                    <li key={index}>
                      {typeof def === 'string' ? (
                        // Format the definition to handle parenthetical content specially
                        formatDefinition(def)
                      ) : (
                        JSON.stringify(def)
                      )}
                    </li>
                  ))}
                </ul>
                
                {/* Add verb information if available and this is a verb section */}
                {wordType.includes('verb') && definition.verb_info && (
                  <div className="chatbot-verb-info">
                    {isInfinitiveVerb ? (
                      // Special display for infinitive verbs
                      <>
                        <p><strong>Verb form:</strong> Infinitive</p>
                        {definition.verb_info.infinitive_form && (
                          <p>
                            <strong>Infinitive:</strong> {typeof definition.verb_info.infinitive_form === 'string' ? 
                              definition.verb_info.infinitive_form : JSON.stringify(definition.verb_info.infinitive_form)}
                          </p>
                        )}
                      </>
                    ) : (
                      // Regular display for conjugated verbs
                      <>
                        {definition.verb_info.person && (
                          <p>
                            <strong>Person:</strong> {typeof definition.verb_info.person === 'string' ? 
                              definition.verb_info.person : JSON.stringify(definition.verb_info.person)}
                          </p>
                        )}
                        {definition.verb_info.tense && (
                          <p>
                            <strong>Tense:</strong> {typeof definition.verb_info.tense === 'string' ? 
                              definition.verb_info.tense : JSON.stringify(definition.verb_info.tense)}
                          </p>
                        )}
                        {definition.verb_info.infinitive_form && (
                          <p>
                            <strong>Infinitive form:</strong> {typeof definition.verb_info.infinitive_form === 'string' ? 
                              definition.verb_info.infinitive_form : JSON.stringify(definition.verb_info.infinitive_form)}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <ul>
              <li>No definition found.</li>
            </ul>
          )}
          
          {/* Context accordion section */}
          {definition.context_note && (
            <div className="chatbot-definition-accordion">
              <div 
                className="chatbot-definition-accordion-header" 
                onClick={() => toggleAccordion('context')}
              >
                <h3 className="chatbot-definition-accordion-title">Context</h3>
                <span className="chatbot-definition-accordion-icon">
                  {openAccordion === 'context' ? '▲' : '▼'}
                </span>
              </div>
              {openAccordion === 'context' && (
                <div className="chatbot-definition-accordion-content">
                  <p className="chatbot-context-note">{definition.context_note}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Grammar accordion section */}
          {definition.grammar_note && (
            <div className="chatbot-definition-accordion">
              <div 
                className="chatbot-definition-accordion-header" 
                onClick={() => toggleAccordion('grammar')}
              >
                <h3 className="chatbot-definition-accordion-title">Grammar</h3>
                <span className="chatbot-definition-accordion-icon">
                  {openAccordion === 'grammar' ? '▲' : '▼'}
                </span>
              </div>
              {openAccordion === 'grammar' && (
                <div className="chatbot-definition-accordion-content">
                  <p className="chatbot-grammar-note">{definition.grammar_note}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return <p>Loading...</p>;
    
    let content;
    if (definition) {
      content = currentLanguage === 'Japanese' 
        ? renderJapaneseDefinition() 
        : renderStandardDefinition();
    } else if (error) {
      content = <p className="chatbot-error-text">Error: {error}</p>;
    } else {
      content = <p>No definition found.</p>;
    }

    return (
      <>
        {content}
        
        <div className="chatbot-word-buttons">
          {!isSaved ? (
            <button onClick={handleSave} className="chatbot-save-btn">Save Word</button>
          ) : (
            <button onClick={handleDelete} className="chatbot-delete-btn">Delete Word</button>
          )}
        </div>
        
        {isSaved && (
          <div className="chatbot-grading">
            <div className="difficulty-scale">
              <div className="difficulty-labels">
                <span className="difficulty-hard">Hard</span>
                <span className="difficulty-easy">Easy</span>
              </div>
              <div className="radio-group">
                {[1, 2, 3, 4, 5].map(grade => (
                  <label key={grade} className={`radio-option radio-grade-${grade} ${wordGrade === grade ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="wordGrade"
                      value={grade}
                      checked={wordGrade === grade}
                      onChange={handleGradeChange}
                    />
                    <span>{grade}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="known-checkbox">
              <input
                type="checkbox"
                id="wordKnown"
                checked={wordKnown}
                onChange={handleKnownChange}
              />
              <div className="known-checkbox-info">
                <label htmlFor="wordKnown" className="known-checkbox-label">
                  I know this word
                </label>
                <div className="known-checkbox-description">
                  Words marked as known won't be highlighted in texts
                </div>
              </div>
            </div>
          </div>
        )}
        
        {error && <div className="chatbot-error-text">{error}</div>}
      </>
    );
  };

  // Check if a character is kanji (Japanese)
  const isKanjiCharacter = (char) => {
    return /[\u4E00-\u9FFF]/.test(char);
  };

  // Get the appropriate class for a word or character
  const getWordClass = () => {
    const isSavedWord = checkSavedStatus();
    
    if (!isSavedWord) {
      return 'chatbot-clickable-word';
    }
    
    let className = 'chatbot-clickable-word chatbot-saved-word';
    
    // Add grade-specific class
    className += ` chatbot-grade-${wordGrade}`;
    
    // If word is marked as known, don't apply saved-word styling
    if (wordKnown) {
      className = 'chatbot-clickable-word';
    }
    
    return className;
  };

  // Render a word with appropriate styling, handling kanji characters
  const renderWordWithStyles = (text) => {
    // For languages that don't have special character handling like kanji
    if (currentLanguage !== 'Japanese') {
      return (
        <span className={getWordClass()}>
          {text}
        </span>
      );
    }
    
    // Special handling for Japanese words that may contain kanji
    return (
      <span className="chatbot-clickable-word">
        {Array.from(text).map((char, idx) => {
          // Non-kanji characters share the word's class
          if (!isKanjiCharacter(char)) {
            return (
              <span key={idx} className={getWordClass()}>
                {char}
              </span>
            );
          }
          
          // For kanji, check if this particular kanji is saved
          const kanjiSaved = isKanjiSaved(char);
          const kanjiKnown = isKanjiKnown(char);
          const kanjiGrade = getKanjiGrade(char) || 1;
          
          let kanjiClass = 'chatbot-clickable-word';
          
          if (kanjiSaved) {
            kanjiClass += ' chatbot-saved-word';
            kanjiClass += ` chatbot-grade-${kanjiGrade}`;
            
            if (kanjiKnown) {
              kanjiClass = 'chatbot-clickable-word';
            }
          }
          
          return (
            <span key={idx} className={kanjiClass}>
              {char}
            </span>
          );
        })}
      </span>
    );
  };

  // Auto-fetch definition when used in dictionary panel
  useEffect(() => {
    // If this component is used in the dictionary panel (onSelectWord is null),
    // automatically fetch the definition for the word
    if (onSelectWord === null && (!definition || normalizedWord !== lastClickedWord)) {
      logger.debugComponent('chatbot', `Auto-fetching definition for "${normalizedWord}" in dictionary panel`);
      setLastClickedWord(normalizedWord);
      fetchDefinition();
    }
  }, [word, onSelectWord, definition, normalizedWord, lastClickedWord]); // Include all dependencies

  // For dictionary panel display
  if (onSelectWord === null) {
    return (
      <div className="chatbot-dictionary-word-definition">
        {renderContent()}
      </div>
    );
  }

  // For clickable words in chatbot messages
  return (
    <span onClick={handleClick}>
      {renderWordWithStyles(children || word)}
    </span>
  );
};

export default ChatbotWordLookup; 